/**
 * Hook to retrieve the vulnerability status display mode from user settings.
 *
 * Uses TanStack Query to fetch settings with automatic cache sharing across
 * multiple components. Multiple hook calls share the same cache entry.
 *
 * Modes:
 * - legacy: Uses traditional labels (Open, Pending) - hides Triaged vulnerabilities
 * - new: Uses updated labels (Demonstrated, Detected) - shows all vulnerabilities
 */

import { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import type { VulnerabilityStatusMode } from '@/utils/vulnerabilityStatus.util';
import { isValidMode } from '@/utils/vulnerabilityStatus.util';

const SETTING_NAME = 'vulnerability-status-system';
const DEFAULT_MODE: VulnerabilityStatusMode = 'new';

export interface UseVulnerabilityStatusModeReturn {
  /** Current mode: 'legacy' | 'new' */
  mode: VulnerabilityStatusMode;
  /** True when using new terminology (Detected/Demonstrated) */
  isNewMode: boolean;
  /** True when using legacy terminology (Open/Pending) */
  isLegacyMode: boolean;
  /** True while settings are loading */
  isLoading: boolean;
  /** Error if settings fetch failed */
  error: Error | null;
}

/**
 * Hook to retrieve the vulnerability status display mode from user settings.
 *
 * Uses TanStack Query to fetch settings with automatic cache sharing across
 * multiple components. Multiple hook calls share the same cache entry.
 *
 * @returns Mode state with loading and error flags
 *
 * @example
 * ```tsx
 * const { mode, isNewMode, isLoading } = useVulnerabilityStatusMode();
 *
 * if (isLoading) return <Skeleton />;
 *
 * return <span>{getStatusLabel(risk.status, mode)}</span>;
 * ```
 */
export function useVulnerabilityStatusMode(): UseVulnerabilityStatusModeReturn {
  const { data: settings, isLoading, error } = useMy({ resource: 'setting' });

  const mode = useMemo<VulnerabilityStatusMode>(() => {
    const setting = (settings ?? []).find(
      (s: { name: string }) => s.name === SETTING_NAME
    );
    const rawValue = setting?.value;

    // Validate and default to new mode if invalid
    return isValidMode(rawValue) ? rawValue : DEFAULT_MODE;
  }, [settings]);

  return {
    mode,
    isNewMode: mode === 'new',
    isLegacyMode: mode === 'legacy',
    isLoading,
    error: error as Error | null,
  };
}
